import { Http, baseApi, RtkqRequest, rtkq, ApiTagType, asky, ListSegment } from '@app/base';
import {
  AddOptionRequest,
  ArchiveOptionRequest,
  DeleteOptionRequest,
  Option,
  UpdateOptionRequest,
} from './optionsTypes';
import { FilterRule, PagingRule, SortRule } from '@webinex/asky';
import { GetSpecialtiesListSegmentDocument } from './graphql';

type OptionRtkqRequest<T> = RtkqRequest<T> & { discriminator: string };

const getSearchFilter = (searchValue?: string | null) => {
  return searchValue
    ? ({
        operator: 'or',
        children: [
          {
            fieldId: 'name',
            operator: 'contains',
            value: searchValue,
          },
          {
            fieldId: 'description',
            operator: 'contains',
            value: searchValue,
          },
        ],
      } satisfies FilterRule)
    : undefined;
};

function mapOptionTagType(discriminator: string) {
  if (discriminator === 'specialty') {
    return discriminator as ApiTagType;
  }

  return `main:${discriminator}` as ApiTagType;
}

function mapOptionInvalidateTags(discriminator: string, id?: string) {
  const type = mapOptionTagType(discriminator);

  if (!id) {
    return [{ type, id: 'list' }];
  }

  return [
    { type, id: 'list' },
    { type, id },
  ];
}

export const optionsApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    getOptionList: builder.query<
      Option[],
      OptionRtkqRequest<{
        filterRule?: FilterRule | null;
        sortRule?: SortRule | null;
        pagingRule?: PagingRule | null;
      }>
    >({
      queryFn: rtkq.axios(({ discriminator, ...args }) =>
        Http.get<Option[]>(`/api/${discriminator}`, {
          params: {
            query: JSON.stringify({
              ...args,
            }),
          },
        }),
      ),
      providesTags: (result = [], _ = {}, args = { discriminator: '' }) => [
        { type: args.discriminator as ApiTagType, id: 'list' },
        ...result.map(({ id }) => ({
          type: args.discriminator as ApiTagType,
          id,
        })),
      ],
    }),

    getSpecialtiesListSegment: builder.query<
      ListSegment<Option>,
      OptionRtkqRequest<{
        searchValue?: string | null;
        filterRule?: FilterRule | null;
        sortRule?: SortRule<string> | null;
        pagingRule?: PagingRule | null;
      }>
    >({
      queryFn: rtkq.graphql(GetSpecialtiesListSegmentDocument, {
        select: (x) => x.specialtiesListSegment,
        vars: ({ searchValue, ...query }) => {
          const searchFilter = getSearchFilter(searchValue);
          const filterRule = asky.filter.create([searchFilter || false, query.filterRule || false]);
          const queryWithSearch = {
            ...query,
            filterRule,
          };
          return {
            query: queryWithSearch,
          };
        },
      }),
      providesTags: (result = { items: [], total: -1 }, _ = {}, args = { discriminator: '' }) => [
        { type: args.discriminator as ApiTagType, id: 'list' },
        ...result.items.map(({ id }) => ({ type: args.discriminator as ApiTagType, id })),
      ],
    }),

    getOption: builder.query<Option, OptionRtkqRequest<{ id: string }>>({
      queryFn: rtkq.axios((args) => Http.sw().get(`/api/${args.discriminator}/${args.id}`)),
      providesTags: (_1, _2, { id, discriminator }) => [{ type: discriminator as ApiTagType, id }],
    }),

    addOption: builder.mutation<string, OptionRtkqRequest<AddOptionRequest>>({
      queryFn: rtkq.axios(({ discriminator, ...args }) => Http.post<string>(`/api/${discriminator}`, args)),
      invalidatesTags: (_1, _2, { discriminator }) => mapOptionInvalidateTags(discriminator),
    }),

    updateOption: builder.mutation<void, OptionRtkqRequest<UpdateOptionRequest>>({
      queryFn: rtkq.axios(({ discriminator, ...args }) => Http.put(`/api/${discriminator}/${args.id}`, args)),
      invalidatesTags: (_1, _2, { discriminator, id }) => mapOptionInvalidateTags(discriminator, id),
    }),

    archiveOption: builder.mutation<void, OptionRtkqRequest<ArchiveOptionRequest>>({
      queryFn: rtkq.axios((args) => Http.put(`/api/${args.discriminator}/${args.id}/archive`, args)),
      invalidatesTags: (_1, _2, { discriminator, id }) => mapOptionInvalidateTags(discriminator, id),
    }),

    deleteOption: builder.mutation<void, OptionRtkqRequest<DeleteOptionRequest>>({
      queryFn: rtkq.axios(({ discriminator, id }) => Http.delete<void>(`/api/${discriminator}/${id}`)),
      invalidatesTags: (_1, _2, { discriminator, id }) => mapOptionInvalidateTags(discriminator, id),
    }),
  }),
});

export const {
  useGetOptionListQuery,
  useLazyGetOptionListQuery,
  useGetOptionQuery,
  useArchiveOptionMutation,
  useAddOptionMutation,
  useDeleteOptionMutation,
  useUpdateOptionMutation,
  useLazyGetSpecialtiesListSegmentQuery,
  useGetSpecialtiesListSegmentQuery,
} = optionsApi;
